<script setup>
// The side preview of the Polaris AI pages: one component in the layout of every page of the assistant, folded
// into a rail or open at the right. Two tabs: Conversation (talk to the draft of the Max's script and see why it
// answered as it did; without a script, the playground as it always was) and Voice ("Call me", coming in the next
// step). Folded or open is remembered in this browser (usePreviewPanel); until the person chooses, the page's
// `defaultOpen` decides (edit pages open, reading pages folded). Shown from 1024px; below that there is no room.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { ChevronRight16, Phone16, Reset16, View16 } from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import AssistantPlayground from 'dashboard/components-next/polaris/assistant/AssistantPlayground.vue';
import UpcomingButton from 'dashboard/components-next/polaris/cards/UpcomingButton.vue';
import PreviewScriptChat from './PreviewScriptChat.vue';
import { usePreviewPanel } from './usePreviewPanel';

const props = defineProps({
  defaultOpen: { type: Boolean, default: true },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isOpen, setOpen, tab, setTab } = usePreviewPanel();

const assistantId = computed(() => Number(route.params.assistantId));
const assistantName = computed(
  () => store.getters['polarisAssistants/getRecord'](assistantId.value)?.name
);
const open = computed(() => isOpen(props.defaultOpen));

const loadedFor = computed(
  () => store.getters['polarisScenarioSet/getAssistantId']
);
const hasSet = computed(() => store.getters['polarisScenarioSet/hasSet']);
const fetching = computed(
  () => store.getters['polarisScenarioSet/getUIFlags'].fetching
);
const known = computed(() => loadedFor.value === assistantId.value);

// The panel needs to know whether the assistant has a script: it asks once per assistant, when it opens. A
// failed ask is the same as "no script": the playground answers.
watch(
  [open, assistantId],
  ([isShown, id]) => {
    if (!isShown || !id || (loadedFor.value === id && !fetching.value)) return;
    if (fetching.value) return;
    store.dispatch('polarisScenarioSet/get', id).catch(() => {});
  },
  { immediate: true }
);

const scriptChat = ref(null);
const playground = ref(null);
const reset = () => (hasSet.value ? scriptChat : playground).value?.reset();
</script>

<template>
  <aside
    v-if="open"
    class="hidden h-full w-80 shrink-0 flex-col gap-3 overflow-hidden border-0 border-s border-solid border-polaris-border bg-n-surface-1 p-4 lg:flex xl:w-96"
    :aria-label="t('POLARIS.PREVIEW.TITLE')"
    data-preview-panel
  >
    <div class="flex shrink-0 items-center justify-between gap-2">
      <h2 class="type-polaris-card-title">{{ t('POLARIS.PREVIEW.TITLE') }}</h2>
      <div class="flex items-center gap-1">
        <Button
          v-if="tab === 'chat'"
          type="button"
          variant="ghost"
          size="sm"
          @click="reset"
        >
          <Reset16 aria-hidden="true" />
          {{ t('POLARIS.PAGES.PLAYGROUND.RESTART') }}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          :aria-label="t('POLARIS.PREVIEW.COLLAPSE')"
          data-preview-collapse
          @click="setOpen(false)"
        >
          <ChevronRight16 aria-hidden="true" class="rtl:rotate-180" />
        </Button>
      </div>
    </div>
    <Tabs :model-value="tab" class="shrink-0" @update:model-value="setTab">
      <TabsList class="w-full">
        <TabsTrigger value="chat" class="flex-1">
          {{ t('POLARIS.PREVIEW.TABS.CHAT') }}
        </TabsTrigger>
        <TabsTrigger value="voice" class="flex-1">
          {{ t('POLARIS.PREVIEW.TABS.VOICE') }}
        </TabsTrigger>
      </TabsList>
    </Tabs>
    <p
      class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
      data-preview-notice
    >
      {{ t('POLARIS.PREVIEW.NOTICE') }}
    </p>
    <div class="min-h-0 flex-1">
      <template v-if="tab === 'chat'">
        <PreviewScriptChat
          v-if="hasSet && known"
          ref="scriptChat"
          :assistant-id="assistantId"
          :assistant-name="assistantName"
        />
        <AssistantPlayground
          v-else-if="known && !fetching"
          ref="playground"
          :assistant-id="assistantId"
          :assistant-name="assistantName"
          class="h-full"
        />
      </template>
      <Empty v-else class="h-full border-0" data-preview-voice>
        <EmptyHeader>
          <EmptyMedia variant="icon"><Phone16 aria-hidden="true" /></EmptyMedia>
          <EmptyTitle>{{ t('POLARIS.PREVIEW.VOICE.TITLE') }}</EmptyTitle>
          <EmptyDescription>
            {{ t('POLARIS.PREVIEW.VOICE.DESCRIPTION') }}
          </EmptyDescription>
        </EmptyHeader>
        <UpcomingButton
          variant="default"
          :label="t('POLARIS.PREVIEW.VOICE.CALL_ME')"
        >
          <Phone16 aria-hidden="true" />
          {{ t('POLARIS.PREVIEW.VOICE.CALL_ME') }}
        </UpcomingButton>
      </Empty>
    </div>
  </aside>
  <div
    v-else
    class="hidden h-full w-12 shrink-0 flex-col items-center border-0 border-s border-solid border-polaris-border bg-n-surface-1 py-4 lg:flex"
    data-preview-rail
  >
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      :aria-label="t('POLARIS.PREVIEW.EXPAND')"
      data-preview-expand
      @click="setOpen(true)"
    >
      <View16 aria-hidden="true" />
    </Button>
  </div>
</template>
